import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchDataGet } from "@/core/api/api-methods";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import {
  createPreviewActions,
  ShareablePreview,
  type ShareablePreviewFeeLine,
} from "@/core/components/shared/preview-action";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import {
  DEFAULT_CURRENCY,
  majorToMinor,
  minorToMajor,
} from "@/core/utils/func/money-minor";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type {
  QrPaymentFeeQuoteResponseDto,
  ResolvedQrCodeDto,
} from "@/features/merchant/api/dto";
import { useQrPaymentFeeQuote } from "@/features/merchant/api/use-qr-payment-fee-quote";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import type { InitiateTransactionRequestDto } from "@/features/wallet/api/dto";
import { useInitiateTransaction } from "@/features/wallet/api/use-initiate-transaction";

/**
 * Parcours scan-to-pay depuis un QR marchand.
 *
 * Étapes :
 * 1. saisie du `payloadToken` → résolution via `GET /QrCodes/{payloadToken}`
 *    (renvoie merchantId, settlementWalletId, montant éventuel…) ;
 * 2. saisie du portefeuille source + montant si le QR est statique ;
 * 3. devis `POST /fee-quotes` puis ShareablePreview (section frais, cahier §9.5) ;
 * 4. initiation via `POST /Transactions/initiate` (wallet-service) avec
 *    `type = MERCHANT_QR_PAYMENT` et l'`Idempotency-Key` persistée par
 *    `useInitiateTransaction` ; navigation vers le reçu.
 */
export function QrScanToPayDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const { i18n } = useTranslation();
  // Placeholder : la mémoïsation par langue n'a pas d'impact ici (pas de
  // schéma zod pour l'instant), mais garde la convention si on en ajoute.
  useMemo(() => i18n.language, [i18n.language]);

  const [payloadToken, setPayloadToken] = useState("");
  const [resolved, setResolved] = useState<ResolvedQrCodeDto | null>(null);
  const [resolving, setResolving] = useState(false);
  const [sourceWalletId, setSourceWalletId] = useState("");
  const [amountMajor, setAmountMajor] = useState<number | undefined>(undefined);
  const [preview, setPreview] = useState<{
    payload: InitiateTransactionRequestDto;
    quote: QrPaymentFeeQuoteResponseDto;
  } | null>(null);

  const initiate = useInitiateTransaction();
  const feeQuote = useQrPaymentFeeQuote();

  const currency =
    resolved?.currency?.trim() ||
    DEFAULT_CURRENCY;

  const resolvedAmountMajor = useMemo(() => {
    if (resolved?.amountMinor != null)
      return minorToMajor(resolved.amountMinor, currency);
    return undefined;
  }, [resolved, currency]);

  const effectiveAmountMajor = resolvedAmountMajor ?? amountMajor ?? 0;

  const handleResolve = async () => {
    const token = payloadToken.trim();
    if (!token) return;
    setResolving(true);
    try {
      const data = await fetchDataGet<ResolvedQrCodeDto>({
        url: merchantEndpoints.qrCodes.byToken(token),
      });
      setResolved(data);
    } catch (error) {
      notifSilenceError(apiErrorHandler(error));
    } finally {
      setResolving(false);
    }
  };

  const handleContinue = () => {
    if (!resolved?.merchantId || !resolved.settlementWalletId) return;
    if (!sourceWalletId.trim()) return;
    if (!(effectiveAmountMajor > 0)) return;
    const amountMinor = majorToMinor(effectiveAmountMajor, currency);
    const payload: InitiateTransactionRequestDto = {
      type: "MERCHANT_QR_PAYMENT",
      productCode: "MERCHANT_QR_PAYMENT",
      sourceWalletId: sourceWalletId.trim(),
      destinationWalletId: resolved.settlementWalletId,
      destinationExternalType: "NONE",
      destinationExternalRef: null,
      amountMinor,
      currency,
      customerSegment: "INDIVIDUAL",
      partyId: null,
      promoCode: null,
      customerReference: resolved.payloadToken ?? token(payloadToken),
    };
    feeQuote.mutate(
      {
        productCode: payload.productCode,
        providerId: null,
        customerSegment: "INDIVIDUAL",
        feePurpose: "CUSTOMER_FEE",
        amountMinor,
        currency,
        // Devis avant initiate : pas de référence wallet `FPT-…`.
        transactionReference: crypto.randomUUID(),
        beneficiaryPartyId: null,
        promoCode: payload.promoCode ?? null,
        partyId: payload.partyId ?? null,
      },
      {
        onSuccess: (quote) => setPreview({ payload, quote }),
        onError: (error) =>
          notifSilenceError(
            apiErrorHandler(error) || tm("merchant.qrCodes.fees.error")
          ),
      }
    );
  };

  const handleConfirm = () => {
    if (!preview) return;
    initiate.mutate(preview.payload, {
      onSuccess: (transaction) => {
        notifSilence(tm("merchant.qrCodes.feedback.paymentInitiated"));
        setPreview(null);
        setResolved(null);
        setPayloadToken("");
        setSourceWalletId("");
        setAmountMajor(undefined);
        onOpenChange(false);
        if (transaction?.id)
          navigate(`/app/wallet/transactions/${transaction.id}`);
      },
      onError: (error) => notifSilenceError(apiErrorHandler(error)),
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {tm("merchant.qrCodes.scanToPay.title")}
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-muted-foreground">
            {tm("merchant.qrCodes.scanToPay.description")}
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="qr-token">
                {tm("merchant.qrCodes.scanToPay.tokenLabel")}
              </Label>
              <Input
                id="qr-token"
                className="notranslate"
                value={payloadToken}
                placeholder={tm(
                  "merchant.qrCodes.scanToPay.tokenPlaceholder"
                )}
                onChange={(e) => setPayloadToken(e.target.value)}
              />
            </div>

            <div className="sm:col-span-2 flex justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={resolving || !payloadToken.trim()}
                onClick={handleResolve}
              >
                {resolving ? <ButtonSpinner /> : null}
                {tm("merchant.qrCodes.scanToPay.resolve")}
              </Button>
            </div>

            {resolved ? (
              <Card className="sm:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base">
                    {tm("merchant.qrCodes.scanToPay.merchant")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {tm("merchant.qrCodes.scanToPay.merchant")}
                    </p>
                    <NoTranslate as="div" className="font-medium">
                      {resolved.merchantName ?? resolved.merchantId}
                    </NoTranslate>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {tm("merchant.qrCodes.scanToPay.settlementWallet")}
                    </p>
                    <NoTranslate as="div" className="font-mono text-xs">
                      {resolved.settlementWalletId ?? "—"}
                    </NoTranslate>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {tm("merchant.qrCodes.scanToPay.qrStatus")}
                    </p>
                    <NoTranslate as="span">
                      <Badge variant="outline">{resolved.status ?? "—"}</Badge>
                    </NoTranslate>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <p className="sm:col-span-2 text-xs text-muted-foreground">
                {tm("merchant.qrCodes.scanToPay.notResolved")}
              </p>
            )}

            {resolved ? (
              <>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="source-wallet-id">
                    {tm("merchant.qrCodes.scanToPay.sourceWalletId")}
                  </Label>
                  <Input
                    id="source-wallet-id"
                    className="notranslate"
                    value={sourceWalletId}
                    placeholder={tm(
                      "merchant.qrCodes.scanToPay.sourceWalletIdPlaceholder"
                    )}
                    onChange={(e) => setSourceWalletId(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="qr-scan-amount">
                    {tm("merchant.qrCodes.scanToPay.amount")}
                  </Label>
                  <Input
                    id="qr-scan-amount"
                    className="notranslate"
                    type="number"
                    min={0}
                    disabled={resolvedAmountMajor != null}
                    value={
                      resolvedAmountMajor != null
                        ? resolvedAmountMajor
                        : amountMajor ?? ""
                    }
                    placeholder={tm(
                      "merchant.qrCodes.scanToPay.amountPlaceholder"
                    )}
                    onChange={(e) => setAmountMajor(e.target.valueAsNumber)}
                  />
                </div>
              </>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={initiate.isPending || feeQuote.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleContinue}
              disabled={
                !resolved?.settlementWalletId ||
                !sourceWalletId.trim() ||
                !(effectiveAmountMajor > 0) ||
                initiate.isPending ||
                feeQuote.isPending
              }
            >
              {feeQuote.isPending ? <ButtonSpinner /> : null}
              {tm("merchant.qrCodes.scanToPay.pay")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview.payload}
          title={tm("merchant.qrCodes.preview.title")}
          description={
            preview.quote.noActiveRule
              ? tm("merchant.qrCodes.preview.noActiveRule")
              : tm("merchant.qrCodes.preview.description")
          }
          mode="user"
          fees={qrQuoteFeeLines(preview.quote, tm)}
          total={{
            label: tm("merchant.qrCodes.preview.totalLabel"),
            amount:
              minorToMajor(
                preview.payload.amountMinor,
                preview.payload.currency
              ) +
              minorToMajor(
                preview.quote.netFeeAmountMinor,
                preview.quote.currency
              ),
            currency: preview.payload.currency,
          }}
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !initiate.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: initiate.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}

/** Petit helper pour taggver le customerReference (évite un getter inline). */
const token = (value: string) => value.trim();

const qrQuoteFeeLines = (
  quote: QrPaymentFeeQuoteResponseDto,
  t: (key: string) => string
): ShareablePreviewFeeLine[] => [
  {
    label: t("merchant.qrCodes.preview.feeGross"),
    amount: minorToMajor(quote.grossFeeAmountMinor, quote.currency),
    currency: quote.currency,
  },
  ...(quote.discountAmountMinor > 0
    ? [
        {
          label: t("merchant.qrCodes.preview.feeDiscount"),
          amount: -minorToMajor(quote.discountAmountMinor, quote.currency),
          currency: quote.currency,
        },
      ]
    : []),
  {
    label: t("merchant.qrCodes.preview.feeNet"),
    amount: minorToMajor(quote.netFeeAmountMinor, quote.currency),
    currency: quote.currency,
  },
];
